import { authEnvironment, type AuthEnvironment } from './environment';

/** Сервер считает вкладку открытой 150 с после видимого пульса — пульс раз в минуту с запасом. */
export const PRESENCE_BEAT_INTERVAL_MS = 60_000;

export interface PresenceReporterOptions {
    /** База API — `config.application.apiUrl` приложения. */
    apiUrl: string;
    getAccessToken: () => string | null;
    /**
     * Пульс «не на экране» в фоне, мс; без него фоновая вкладка молчит до возврата. Вкладка входа «от имени»: по пульсу
     * сервер держит вход открытым и отличает открытую вкладку сотрудника от брошенной.
     */
    hiddenIntervalMs?: number;
    env?: Partial<Pick<AuthEnvironment, 'fetch' | 'setTimer' | 'visibility' | 'activity'>>;
}

export interface PresenceReporter {
    /** Повторный вызов — no-op. */
    start(): void;
    stop(): void;
}

/**
 * Пульс присутствия для страницы «Сеансы» админки: пока вкладка видима — раз в минуту «на экране» и «был ли ввод», при
 * возврате на вкладку — сразу, при уходе в фон — «не на экране» с `keepalive` (один раз, с `hiddenIntervalMs` — и
 * дальше с этим периодом). Сессию пульс не продлевает и идёт мимо клиента приложения: его отказ не должен запускать
 * ротацию, выход или тост.
 */
export function createPresenceReporter({
    apiUrl,
    getAccessToken,
    hiddenIntervalMs,
    env,
}: PresenceReporterOptions): PresenceReporter {
    const resolved = authEnvironment(env);
    let hadInput = true;
    let cancelBeat: (() => void) | null = null;
    let detach: (() => void) | null = null;

    const send = (visible: boolean) => {
        const token = getAccessToken();
        if (!token) return;
        const active = visible && hadInput;
        hadInput = false;
        resolved
            .fetch(`${apiUrl}/Presence`, {
                method: 'POST',
                headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
                body: JSON.stringify({ visible, active }),
                keepalive: !visible,
            })
            .catch(() => undefined);
    };

    const stopBeats = () => {
        cancelBeat?.();
        cancelBeat = null;
    };

    const startBeats = (visible: boolean) => {
        stopBeats();
        const interval = visible ? PRESENCE_BEAT_INTERVAL_MS : hiddenIntervalMs;
        if (!interval) return;
        const beat = () => {
            send(visible);
            cancelBeat = resolved.setTimer(beat, interval);
        };
        cancelBeat = resolved.setTimer(beat, interval);
    };

    const onVisibilityChange = () => {
        const visible = resolved.visibility.isVisible();
        startBeats(visible);
        send(visible);
    };

    return {
        start() {
            if (detach) return;
            // Страница только что открыта или пользователь только что вошёл — это действие пользователя.
            hadInput = true;
            const offActivity = resolved.activity.subscribe(() => {
                hadInput = true;
            });
            const offVisibility = resolved.visibility.subscribe(onVisibilityChange);
            detach = () => {
                offActivity();
                offVisibility();
            };
            const visible = resolved.visibility.isVisible();
            if (visible) send(true);
            startBeats(visible);
        },
        stop() {
            stopBeats();
            detach?.();
            detach = null;
        },
    };
}
